/**
 * GeneralTab — contract_no, contract_date, vendor (cp_code), seller_ref,
 * quality, certification, me/ib, license, quantity, package.
 */
import { Autocomplete, Box, MenuItem, TextField } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { Controller, type Control, type FieldErrors } from 'react-hook-form'

import {
  useListCertificationsQuery,
  useListLicensesQuery,
  useListMeOrIbQuery,
  useListPackagesQuery,
  useListQualitiesQuery,
  useLookupVendorsQuery,
} from '@/store/api/lookupsApi'
import type { LocalPFormValues } from './types'

export interface GeneralTabProps {
  control: Control<LocalPFormValues>
  errors: FieldErrors<LocalPFormValues>
  isEdit: boolean
  cpCode: string
}

export function GeneralTab({ control, errors, isEdit, cpCode }: GeneralTabProps) {
  const { data: vendors = [] } = useLookupVendorsQuery({ q: '' })
  const { data: qualities = [] } = useListQualitiesQuery({ local_only: true })
  const { data: certifications = [] } = useListCertificationsQuery()
  const { data: meOrIbOpts = [] } = useListMeOrIbQuery()
  const { data: packages = [] } = useListPackagesQuery()
  const { data: licenses = [] } = useListLicensesQuery(
    cpCode ? { cp_code: cpCode } : undefined,
    { skip: !cpCode },
  )

  return (
    <Box display="flex" flexDirection="column" gap={2} pt={1}>
      <Box display="flex" gap={2} flexWrap="wrap">
        <Controller
          control={control}
          name="contract_no"
          render={({ field }) => (
            <TextField
              {...field}
              label="Số hợp đồng"
              size="small"
              sx={{ flex: '1 1 220px' }}
              disabled={isEdit}
              placeholder={isEdit ? '' : 'Auto: P-XXXXX'}
              error={!!errors.contract_no}
              helperText={errors.contract_no?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="contract_date"
          render={({ field }) => (
            <DatePicker
              label="Ngày hợp đồng *"
              format="DD/MM/YYYY"
              value={field.value ? dayjs(field.value) : null}
              onChange={(d) => field.onChange(d ? d.format('YYYY-MM-DD') : '')}
              slotProps={{
                textField: {
                  size: 'small',
                  sx: { flex: '1 1 200px' },
                  error: !!errors.contract_date,
                  helperText: errors.contract_date?.message,
                },
              }}
            />
          )}
        />
      </Box>

      <Box display="flex" gap={2} flexWrap="wrap">
        <Controller
          control={control}
          name="cp_code"
          render={({ field }) => {
            const selected =
              vendors.find((v) => v.code === field.value) ??
              (field.value ? { code: field.value, name: '', short_name: null, vat_code: null } : null)
            return (
              <Autocomplete
                size="small"
                sx={{ flex: '2 1 320px' }}
                options={vendors}
                value={selected}
                onChange={(_e, v) => field.onChange(v?.code ?? '')}
                getOptionLabel={(opt) => (opt ? `${opt.code} — ${opt.name ?? ''}` : '')}
                isOptionEqualToValue={(o, v) => o.code === v.code}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Vendor (cp_code) *"
                    error={!!errors.cp_code}
                    helperText={errors.cp_code?.message}
                  />
                )}
              />
            )
          }}
        />
        <Controller
          control={control}
          name="seller_ref"
          render={({ field }) => (
            <TextField
              {...field}
              label="Seller Ref"
              size="small"
              sx={{ flex: '1 1 220px' }}
              error={!!errors.seller_ref}
              helperText={errors.seller_ref?.message}
            />
          )}
        />
      </Box>

      <Box display="flex" gap={2} flexWrap="wrap">
        <Controller
          control={control}
          name="quality_code"
          render={({ field }) => (
            <TextField
              {...field}
              label="Quality"
              select
              size="small"
              sx={{ flex: '1 1 220px' }}
              error={!!errors.quality_code}
              helperText={errors.quality_code?.message}
            >
              <MenuItem value="">— Chưa chọn —</MenuItem>
              {qualities.map((q) => (
                <MenuItem key={q.code} value={q.code}>
                  {q.code} — {q.description}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <Controller
          control={control}
          name="certification"
          render={({ field }) => (
            <TextField
              {...field}
              label="Certification"
              select
              size="small"
              sx={{ flex: '1 1 200px' }}
              error={!!errors.certification}
              helperText={errors.certification?.message}
            >
              <MenuItem value="">— Chưa chọn —</MenuItem>
              {certifications.map((c) => (
                <MenuItem key={c.certification} value={c.certification}>
                  {c.certification}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <Controller
          control={control}
          name="me_or_ib"
          render={({ field }) => (
            <TextField
              {...field}
              label="ME / IB"
              select
              size="small"
              sx={{ flex: '1 1 200px' }}
              error={!!errors.me_or_ib}
              helperText={errors.me_or_ib?.message}
            >
              <MenuItem value="">— Chưa chọn —</MenuItem>
              {meOrIbOpts.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Box>

      <Box display="flex" gap={2} flexWrap="wrap">
        <Controller
          control={control}
          name="license_cert_no"
          render={({ field }) => (
            <TextField
              {...field}
              label="License Cert No"
              select
              size="small"
              sx={{ flex: '1 1 240px' }}
              error={!!errors.license_cert_no}
              helperText={
                errors.license_cert_no?.message ?? (cpCode ? '' : 'Chọn vendor trước')
              }
              disabled={!cpCode}
            >
              <MenuItem value="">— Chưa chọn —</MenuItem>
              {licenses.map((l) => (
                <MenuItem key={String(l.license_id)} value={l.license_cert_no}>
                  {l.license_cert_no}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
        <Controller
          control={control}
          name="quantity_kg"
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value === '' ? '' : field.value}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : Number(e.target.value))
              }
              label="Quantity (kg) *"
              type="number"
              size="small"
              sx={{ flex: '1 1 180px' }}
              error={!!errors.quantity_kg}
              helperText={errors.quantity_kg?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="no_of_bag"
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value === '' ? '' : field.value}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? '' : Number(e.target.value))
              }
              label="Số bao"
              type="number"
              size="small"
              sx={{ flex: '1 1 140px' }}
              error={!!errors.no_of_bag}
              helperText={errors.no_of_bag?.message}
            />
          )}
        />
        <Controller
          control={control}
          name="package"
          render={({ field }) => (
            <TextField
              {...field}
              label="Package"
              select
              size="small"
              sx={{ flex: '1 1 200px' }}
              error={!!errors.package}
              helperText={errors.package?.message}
            >
              <MenuItem value="">— Chưa chọn —</MenuItem>
              {packages.map((p) => (
                <MenuItem key={p.value} value={p.value}>
                  {p.label}
                </MenuItem>
              ))}
            </TextField>
          )}
        />
      </Box>
    </Box>
  )
}

export default GeneralTab
